import entries from "lodash/entries";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import sumBy from "lodash/sumBy";
import { useEdgeToEdgeLayout } from "PFApp/app_shell_layout.utils";
import { MultiActivityFormProvider } from "PFApp/components/activity_form/multi_activity_form/multi_activity_form_provider";
import { ValidationAlert } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/parts/validation_alert/validation_alert";
import { PageFooter } from "PFApp/components/page_footer";
import {
  FIELD_ID_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { getFieldReferenceKey } from "PFApp/helpers/activity_form/get_field_reference_key";
import { getRolesFormKey } from "PFApp/hooks/activity_form/multi_roles_form/use_roles_field_array";
import { useSubtemplatesFieldArray } from "PFApp/hooks/activity_form/multi_roles_form/use_subtemplates_field_array";
import { useTransformFormToBulkOperations } from "PFApp/hooks/activity_form/multi_roles_form/use_transform_form_to_bulk_operations";
import { useIsFormChanged } from "PFApp/hooks/activity_form/use_is_form_changed";
import {
  MultiActivityFormStoreProvider,
  useMultiActivityFormStore
} from "PFApp/stores/activity_form/multi_roles_form";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  ActivityFormType,
  MultiActivityForm,
  RoleFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { MovableDivider } from "PFComponents/movable_divider";
import { RouterPrompt } from "PFComponents/router_prompt/router_prompt";
import { Typography } from "PFComponents/typography";
import { usePusherEvent } from "PFCore/base/pusher";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBulkOperationsCreate, useBulkOperationsItems } from "PFCore/hooks/queries/bulk_operations";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { useSubtemplates } from "PFCore/hooks/queries/subtemplates/use_subtemplates";
import { BulkOperation, BulkOperationsState } from "PFCore/services/bulk_operations";
import { Activity, EngagementBlueprint, Id } from "PFTypes";
import { Fragment, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ProcessingOverlay } from "../../components/activity_form/multi_activity_form/processing_overlay";
import css from "./apply_engagement_blueprint_page.module.scss";
import { SubtemplateContainer } from "./parts/subtemplate_container";
import { Toolbar } from "./parts/toolbar";

const INITIAL_LEFT_WIDTH = 80;
const MIN_LEFT_WIDTH = 20;
const MIN_RIGHT_WIDTH = 10;

const TERMINAL_BULK_OPERATIONS_STATES = [
  BulkOperationsState.Completed,
  BulkOperationsState.Failed,
  BulkOperationsState.PartiallyCompleted
];

type ApplyEngagementBlueprintPageProps = {
  parentActivity: Activity;
  engagementBlueprint: EngagementBlueprint;
  engagementBlueprintActivities: Activity[];
};

type BulkOperationsPusherData = {
  id: Id;
  state: BulkOperationsState;
};

const ApplyEngagementBlueprintPageContent = ({ engagementBlueprint }: ApplyEngagementBlueprintPageProps) => {
  useEdgeToEdgeLayout();
  const { t: tTranslation } = useTranslation();
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { getFirstError } = useResponseErrors();
  const navigate = useNavigate();
  const { transformFormToBulkOperations } = useTransformFormToBulkOperations();
  const isChanged = useIsFormChanged({ simplified: true });
  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const { invalidateRoles } = useRolesInvalidate();
  const { handleSubmit, setValue, setError, clearErrors, getValues, resetField } =
    useFormContext<MultiActivityForm>();
  const scrollableContainerRef = useMultiActivityFormStore((store) => store.scrollableContainerRef);
  const parentActivity = useMultiActivityFormStore((store) => store.parentActivity);

  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(INITIAL_LEFT_WIDTH);
  const [bulkOperationsStatus, setBulkOperationsStatus] = useState<BulkOperationsState | null>(null);

  const {
    mutateAsync: performBulkOperationsCreate,
    data: bulkOperationsCreateData,
    isPending: isCreatingBulkOperations
  } = useBulkOperationsCreate();

  usePusherEvent(
    `private-profile-${currentProfile.id}`,
    "bulk_operation_completed",
    ({ id, state }: BulkOperationsPusherData) => {
      if (id === bulkOperationsCreateData?.id) {
        setBulkOperationsStatus(state);
      }
    },
    !!bulkOperationsCreateData?.id
  );

  const { data: bulkOperationsItemsData, isFetching: isFetchingBulkOperationsItems } = useBulkOperationsItems<
    ActivityFormType,
    RoleFormKey
  >(
    bulkOperationsCreateData?.id || -1,
    { page: 1, perPage: PER_PAGE_UNPAGINATED },
    {
      enabled:
        !!bulkOperationsCreateData?.id &&
        !!bulkOperationsStatus &&
        TERMINAL_BULK_OPERATIONS_STATES.includes(bulkOperationsStatus)
    }
  );

  useEffect(() => {
    if (!bulkOperationsItemsData) {
      return;
    }
    const bulkOperationsItems = bulkOperationsItemsData.entries;
    const hasErrors = bulkOperationsItems.some(({ state }) => state === BulkOperationsState.Failed);
    if (hasErrors) {
      showGrowl({ message: t("submitError"), kind: "error" });
      bulkOperationsItems.forEach(({ resourceId, itemIdentifier, processingErrors }) => {
        if (resourceId) {
          // Push the created role id to the form, so its in "update" operation on next submit
          // For MVP: id presence is used to disable the row (we don't handle editing)
          setValue(`${itemIdentifier}.${FIELD_ID_KEY}`, resourceId);
          // Make the saved role not dirty to display a proper status badge
          resetField(itemIdentifier, { defaultValue: getValues(itemIdentifier) });
        }
        entries(processingErrors).forEach(([field, error]) => {
          const referenceKey = getFieldReferenceKey(itemIdentifier, field, getValues());

          setError(referenceKey as any, {
            message: isArray(error)
              ? error
                  .map((errorItem) => (isObject(errorItem) ? JSON.stringify(errorItem) : errorItem))
                  .join(", ")
              : ""
          });
        });
      });
    } else {
      showGrowl({ message: t("submitSuccess") });
      navigate(`/activities/${parentActivity!.id}/roles`);
    }
    invalidateRoles();
  }, [bulkOperationsItemsData]);

  const { data: subtemplates = [] } = useSubtemplates({ templateKey: TemplateKey.Role });

  const { subtemplatesFields, addRoleInSubtemplate, removeSubtemplate } = useSubtemplatesFieldArray();

  const rolesCount = useWatch({
    name: FORM_SUBTEMPLATES_KEY,
    defaultValue: [],
    compute: (data) =>
      sumBy(
        data,
        (subtemplateData: MultiActivityForm["subtemplates"][number]) => subtemplateData[FORM_ROLES_KEY].length
      )
  });

  const handleCancel = () => {
    navigate(`/activities/${parentActivity!.id}/roles`);
  };

  const handleFormSubmit = async (formValues: MultiActivityForm) => {
    setBulkOperationsStatus(BulkOperationsState.Queued);
    const bulkOperations = transformFormToBulkOperations(formValues, {}, [], false, false);

    await performBulkOperationsCreate({
      operation: BulkOperation.ApplyTeamTemplate,
      items: bulkOperations
    }).catch((error) => {
      setBulkOperationsStatus(null);
      showGrowl({ message: getFirstError(error) ?? tTranslation("somethingWentWrong"), kind: "error" });
    });
  };

  const handleSaveClick = () => {
    clearErrors();
    handleSubmit(handleFormSubmit)();
  };

  const leftPanelWidthCss = `${leftPanelWidth}%`;
  const isLoading =
    isCreatingBulkOperations ||
    (bulkOperationsStatus &&
      [BulkOperationsState.Processing, BulkOperationsState.Queued].includes(bulkOperationsStatus)) ||
    isFetchingBulkOperationsItems;
  const isSuccess = !!bulkOperationsItemsData?.entries.every(
    ({ state }) => state === BulkOperationsState.Completed
  );

  return (
    <Stack gap={null}>
      <RouterPrompt when={isChanged && !isSuccess} />
      {isLoading && (
        <ProcessingOverlay title={t("preparingRoles")} subtitle={t("preparingRolesDescription")} />
      )}
      <Stack as="header" justifyContent="center" gap={null}>
        <Typography variant="h2" className={css.title}>
          {t("header")}
        </Typography>
        <ValidationAlert />
        <Toolbar
          rolesCount={rolesCount}
          addRoleInSubtemplate={addRoleInSubtemplate}
          blueprintName={engagementBlueprint.name}
        />
      </Stack>
      <Stack ref={scrollableContainerRef} className={css.formContainer}>
        <Stack ref={containerRef} className={css.content} gap="spacing3xl">
          {subtemplatesFields.map((field, index) => {
            const subtemplate = subtemplates.find(
              ({ subtemplate_key }) => subtemplate_key === field.subtemplate_key
            );

            if (!subtemplate && field[FIELD_SUBTEMPLATE_KEY] !== NO_SUBTEMPLATE_VALUE) {
              return null;
            }

            const rolesFormKey = getRolesFormKey(index);

            return (
              <Fragment key={field.id}>
                <SubtemplateContainer
                  rolesFormKey={rolesFormKey}
                  subtemplate={subtemplate ?? null}
                  leftPanelWidthCss={leftPanelWidthCss}
                  removeSubtemplate={() => removeSubtemplate(index)}
                />
                {index < subtemplatesFields.length - 1 && <Divider />}
              </Fragment>
            );
          })}
          {rolesCount > 0 && (
            <MovableDivider
              qaId="EngagementBlueprintMovableDivider.Handle"
              containerElement={containerRef.current}
              initialLeftWidth={INITIAL_LEFT_WIDTH}
              minLeftWidth={MIN_LEFT_WIDTH}
              minRightWidth={MIN_RIGHT_WIDTH}
              onResize={setLeftPanelWidth}
              unit="percent"
              className={css.movableDivider}
              style={{ left: leftPanelWidthCss }}
            />
          )}
        </Stack>
      </Stack>
      <PageFooter>
        <Button kind="secondary" text={tTranslation("cancel")} onClick={handleCancel} disabled={isLoading} />
        <Button kind="primary" text={tTranslation("save")} onClick={handleSaveClick} disabled={isLoading} />
      </PageFooter>
    </Stack>
  );
};

export const ApplyEngagementBlueprintPage = (props: ApplyEngagementBlueprintPageProps) => (
  <MultiActivityFormStoreProvider parentActivity={props.parentActivity}>
    <MultiActivityFormProvider
      initialActivities={props.engagementBlueprintActivities}
      isEachInitialActivityNew
      forceInterestQuestionsFromActivity
    >
      <ApplyEngagementBlueprintPageContent {...props} />
    </MultiActivityFormProvider>
  </MultiActivityFormStoreProvider>
);
